import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { X, AlertTriangle } from "lucide-react";

// Iter 83 + Iter 128 — 90-day settlement panel.
// Delivered wager is entered PER CATEGORY (mirroring contract formation:
// 6 casino categories, case coefficient family for case_opening). The
// weighted delivered total = Σ(category × coefficient) using the SAME
// coefficient config the contract was formed on. That weighted number
// feeds shortfall/over/refund math (unchanged from Iter 83) — no scalar
// input is accepted anymore.
function SettlementPanel({ contract, onSettled, onChanged }) {
  const vertical = contract?.vertical || "casino";
  const [cats, setCats] = useState({});
  const [coeffs, setCoeffs] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const url = vertical === "case_opening" ? "/config/case-coefficients" : "/config/wager-coefficients";
    api.get(url).then((r) => setCoeffs(r.data)).catch(() => setCoeffs({ categories: [] }));
  }, [vertical]);

  // Iter 174 — `agreed_monthly_volume` now stores the monthly figure
  // (post-Iter-173 bounds normalization); the 3-month target is monthly × 3.
  const agreed = Number(contract?.agreed_monthly_volume || 0) * 3;

  // Weighted delivered = Σ(category × coefficient). Same math as formation.
  const weighted = useMemo(() => {
    if (!coeffs) return 0;
    return coeffs.categories.reduce((sum, c) => {
      const v = Number(cats[c.key]);
      if (!Number.isFinite(v)) return sum;
      return sum + v * Number(c.coefficient);
    }, 0);
  }, [cats, coeffs]);
  const delta = weighted - agreed;

  const hasAnyInput = coeffs && coeffs.categories.some((c) => {
    const v = cats[c.key];
    return v !== undefined && v !== "" && !Number.isNaN(Number(v));
  });
  const outcome = !hasAnyInput ? "—"
    : agreed <= 0 ? "—"
    : Math.abs(delta) < 0.5 ? "exact"
    : delta < 0 ? "shortfall (refund)"
    : "over (display-only surcharge)";

  const submit = async () => {
    if (!coeffs || !hasAnyInput) { toast.error("Enter delivered wager per category"); return; }
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    const payload = {};
    for (const c of coeffs.categories) {
      const raw = cats[c.key];
      const v = Number(raw === "" || raw === undefined ? 0 : raw);
      if (!Number.isFinite(v) || v < 0) { toast.error(`${c.key}: must be ≥ 0`); return; }
      payload[c.key] = v;
    }
    setBusy(true);
    try {
      await api.post(`/admin/contracts/${contract.id}/settle`, { deliveredCategories: payload, reason: reason.trim() });
      toast.success("Settlement recorded");
      onSettled && onSettled();
      onChanged && onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-dashed border-[#2DD4A7]/70 bg-[#2DD4A7]/5 p-4 space-y-3" data-testid="admin-contract-settle-panel">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">Create 90-day settlement · per-category delivered</div>
      {!coeffs ? (
        <div className="text-[10px] text-[#8B97AE]">Loading coefficients…</div>
      ) : (coeffs.categories.length === 0) ? (
        <div className="text-[10px] text-[#F26D6D]">No coefficient config for vertical {vertical}.</div>
      ) : (
        <div className="space-y-1" data-testid="settle-category-rows">
          <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 text-[10px] font-mono text-[#8B97AE] uppercase tracking-widest pb-1 border-b border-white/10">
            <span>Category</span>
            <span className="text-right">Delivered</span>
            <span className="text-right">Weighted (× coef)</span>
          </div>
          {coeffs.categories.map((c) => {
            const raw = cats[c.key];
            const v = Number(raw === "" || raw === undefined ? 0 : raw);
            const w = Number.isFinite(v) ? v * Number(c.coefficient) : 0;
            return (
              <div key={c.key} className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 items-center py-0.5 text-xs font-mono">
                <span className="text-[#E8ECF4] capitalize">{c.key.replace(/_/g, " ")}</span>
                <Input
                  data-testid={`settle-delivered-${c.key}`}
                  type="number" step="0.01" min="0"
                  value={cats[c.key] ?? ""}
                  onChange={(e) => setCats((old) => ({ ...old, [c.key]: e.target.value }))}
                  className="rounded-none bg-transparent border-white/20 h-8 font-mono text-right tabular-nums"
                />
                <span className="text-right tabular-nums text-[#8B97AE]">{w.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[9px]">× {c.coefficient}</span></span>
              </div>
            );
          })}
          <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 mt-1 pt-1 border-t border-white/10 text-xs font-mono">
            <span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Delivered weighted total</span>
            <span></span>
            <span className="text-right tabular-nums text-[#2DD4A7]" data-testid="settle-weighted-total">{weighted.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</span>
          </div>
        </div>
      )}
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Reason (required)</Label>
        <Input data-testid="settle-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none bg-transparent border-white/20 h-9 font-mono text-xs" />
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
        <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Agreed × 3</span><div className="text-[#E8ECF4]" data-testid="settle-agreed">{agreed.toLocaleString()} USD</div></div>
        <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Delta</span><div className={delta < 0 ? "text-[#F26D6D]" : delta > 0 ? "text-[#F5B841]" : "text-emerald-400"} data-testid="settle-delta">{delta.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</div></div>
        <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Outcome (preview)</span><div className="text-[#E8ECF4]" data-testid="settle-outcome">{outcome}</div></div>
      </div>
      <Button data-testid="settle-confirm" onClick={submit} disabled={busy || !hasAnyInput || !reason.trim()} className="rounded-none bg-[#2DD4A7] text-black hover:bg-[#F5B841] h-9 font-mono text-[11px] uppercase tracking-widest">Settle contract</Button>
      <div className="text-[10px] font-mono text-[#8B97AE]">Weighted delivered = Σ(category × coefficient), same coefficients as formation. Shortfall → pending settlement_refund payout (screening + mark-paid unchanged). Over-performance → display-only surcharge.</div>
    </div>
  );
}

// Iter 82 (frontend) — Admin full-contract view. Read-only aside from the
// TEST hook (mark-payment-confirmed) and the Iter 83 settlement panel.

function Pill({ tone = "default", children, "data-testid": testId }) {
  const map = {
    default: "border-white/20 text-white",
    ok: "border-emerald-500/50 text-emerald-400",
    warn: "border-[#F5B841]/50 text-[#F5B841]",
    danger: "border-[#F26D6D]/60 text-[#F26D6D]",
    accent: "border-[#2DD4A7]/50 text-[#2DD4A7]",
  };
  return <span data-testid={testId} className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest ${map[tone]}`}>{children}</span>;
}

const stateTone = (s) => ({
  pending_payment: "warn",
  pending_player_confirmation: "accent",
  active: "ok",
  settled: "default",
  declined: "danger",
  expired: "danger",
}[s] || "default");

function DocPanel({ title, doc, testIdPrefix }) {
  if (!doc) return <div className="text-xs text-[#8B97AE]">—</div>;
  const row = (label, val, tone) => (
    <div className="flex justify-between gap-3 py-1 border-b border-white/5 text-xs font-mono">
      <span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">{label}</span>
      <span className={tone === "accent" ? "text-[#2DD4A7]" : tone === "warn" ? "text-[#F5B841]" : "text-[#E8ECF4]"}>{val ?? "—"}</span>
    </div>
  );
  return (
    <div className="border border-white/10 p-3" data-testid={testIdPrefix}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-2">{title}</div>
      {row("Governing law", doc.governingLaw)}
      {row("Dispute", doc.disputeResolution)}
      {row("Party", `${doc.party?.alias || "—"} (${doc.party?.role || "—"})`)}
      {row("Counterparty", `${doc.counterparty?.alias || "—"} (${doc.counterparty?.role || "—"})`)}
      {row("Vertical", doc.vertical)}
      {row("Agreed monthly (× 3 months)", `${Number(doc.agreedMonthlyVolume || 0).toLocaleString()} USD/mo · ${(Number(doc.agreedMonthlyVolume || 0) * 3).toLocaleString()} USD 3-month`, "accent")}
      {row("70% floor", `${Number(doc.floorMinimum || 0).toLocaleString()} USD`, "warn")}
      {Array.isArray(doc.wagerBreakdown) && doc.wagerBreakdown.length > 0 && (
        <div className="mt-3 border-t border-white/10 pt-2" data-testid={`${testIdPrefix}-wager-breakdown`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-1">Agreed wager breakdown</div>
          <div className="grid grid-cols-3 gap-x-2 text-[10px] font-mono text-[#8B97AE] uppercase tracking-widest pb-1 border-b border-white/5">
            <span>Category</span>
            <span className="text-right">Amount</span>
            <span className="text-right">Weighted (× coef)</span>
          </div>
          {doc.wagerBreakdown.map((r) => (
            <div key={r.key} className="grid grid-cols-3 gap-x-2 py-0.5 text-xs font-mono">
              <span className="text-[#E8ECF4]">{r.key.replace(/_/g, " ")}</span>
              <span className="text-right tabular-nums">{Number(r.amount).toLocaleString()}</span>
              <span className="text-right tabular-nums text-[#8B97AE]">{Number(r.weighted).toLocaleString()} <span className="text-[9px]">× {r.coefficient}</span></span>
            </div>
          ))}
          <div className="grid grid-cols-3 gap-x-2 mt-1 pt-1 border-t border-white/10 text-xs font-mono">
            <span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Weighted total</span>
            <span></span>
            <span className="text-right tabular-nums text-[#2DD4A7]">{Number(doc.wagerWeightedTotal || 0).toLocaleString()} USD</span>
          </div>
        </div>
      )}
      {doc.whereToRegister && row(
        "Where to register",
        `${doc.whereToRegister.casino || "—"} · code ${doc.whereToRegister.affiliateCode || "—"}${doc.whereToRegister.referralLink ? " · " + doc.whereToRegister.referralLink : ""}`,
        "accent",
      )}
      {row("Settlement", doc.settlement)}
      {row("Term start", doc.termStart)}
      {row("Signed at", doc.signedBy)}
    </div>
  );
}

export default function AdminContractDetailModal({ contractId, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("player");

  const refresh = async () => {
    if (!contractId) return;
    try {
      const r = await api.get(`/admin/contracts/${contractId}`);
      setC(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [contractId]);

  if (!contractId) return null;
  const state = c?.status || "—";

  const markPaymentConfirmed = async () => {
    if (!c) return;
    if (!window.confirm("TEST HOOK: move this contract from pending_payment → pending_player_confirmation? Payment layer is NOT armed; no real charge.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/contracts/${c.id}/mark-payment-confirmed`);
      toast.success("Marked payment confirmed (TEST)");
      await refresh();
      if (onChanged) onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="admin-contract-modal" onClick={onClose}>
      <div className="w-full max-w-3xl bg-[#0B1220] border border-white/20 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Admin · Contract detail</div>
            <div className="font-display font-bold text-lg text-[#E8ECF4]" data-testid="admin-contract-id">{contractId?.slice(0, 8)}</div>
          </div>
          <button data-testid="admin-contract-close" onClick={onClose} className="text-[#8B97AE] hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>

        {!c ? (
          <div className="p-6 text-xs text-[#8B97AE]">…</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Header row: state + flags */}
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone={stateTone(state)} data-testid="admin-contract-state">{state}</Pill>
              {c.is_test && <Pill tone="warn" data-testid="admin-contract-is-test">TEST</Pill>}
              {c.refund_due && <Pill tone="danger" data-testid="admin-contract-refund-due">REFUND DUE (flag)</Pill>}
              {c.flow && <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">flow: {c.flow}</span>}
            </div>

            {/* Parties + Numbers */}
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-white/10 p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Parties</div>
                <div className="text-xs font-mono space-y-1">
                  <div data-testid="admin-contract-player"><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Player</span> <span className="text-[#E8ECF4]">{c.parties?.player?.alias || "—"}</span><div className="text-[10px] text-[#8B97AE] truncate">{c.parties?.player?.id}</div></div>
                  <div data-testid="admin-contract-streamer"><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Streamer</span> <span className="text-[#E8ECF4]">{c.parties?.streamer?.alias || "—"}</span><div className="text-[10px] text-[#8B97AE] truncate">{c.parties?.streamer?.id}</div></div>
                </div>
              </div>
              <div className="border border-white/10 p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Numbers</div>
                <div className="text-xs font-mono space-y-1">
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Vertical</span> <span className="text-[#E8ECF4]">{c.vertical || "—"}</span></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Agreed monthly</span> <span className="text-[#2DD4A7]">{Number(c.agreed_monthly_volume || 0).toLocaleString()} USD/mo · 90-day term</span></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">70% floor</span> <span className="text-[#F5B841]">{Number(c.floor_minimum || 0).toLocaleString()} USD</span></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Created</span> <span className="text-[#8B97AE]">{c.createdAt || "—"}</span></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Confirm deadline</span> <span className="text-[#8B97AE]">{c.confirm_deadline_at || "—"}</span></div>
                </div>
              </div>
            </div>

            {/* TEST hook (only in pending_payment) */}
            {state === "pending_payment" && (
              <div className="border-2 border-dashed border-[#F5B841] bg-[#F5B841]/5 p-4 space-y-2" data-testid="admin-contract-test-panel">
                <div className="flex items-center gap-2 text-[#F5B841]">
                  <AlertTriangle className="w-4 h-4" />
                  <div className="text-[11px] font-mono uppercase tracking-widest">TEST hook — payment layer NOT armed</div>
                </div>
                <div className="text-[11px] font-mono text-[#8B97AE]">This mimics what the payment processor will call once UNLOCK_PAYMENT_ARMED is flipped. No real charge, no ledger movement.</div>
                <Button
                  data-testid="admin-contract-mark-payment-confirmed"
                  onClick={markPaymentConfirmed}
                  disabled={busy}
                  className="rounded-none bg-[#F5B841] text-[#04150F] hover:bg-[#F5B841]/80 h-9 font-mono text-[11px] uppercase tracking-widest"
                >Mark Payment Confirmed (TEST — payments not armed)</Button>
              </div>
            )}

            {/* Iter 83 — 90-day settlement tool (only in active) */}
            {state === "active" && <SettlementPanel contract={c} onSettled={refresh} onChanged={onChanged} />}

            {/* Iter 83 — After-settlement view */}
            {state === "settled" && c.settlement && (
              <div className="border border-white/15 p-3 space-y-2" data-testid="admin-contract-settlement-view">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Settlement record</div>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Outcome</span><div className={c.settlement.outcome === "shortfall" ? "text-[#F26D6D]" : c.settlement.outcome === "over" ? "text-[#F5B841]" : "text-emerald-400"} data-testid="settlement-outcome">{c.settlement.outcome}</div></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Delivered</span><div>{Number(c.settlement.verified_delivered_wager).toLocaleString()} USD</div></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Agreed × 3</span><div>{Number(c.settlement.agreed_total).toLocaleString()} USD</div></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Delta</span><div>{Number(c.settlement.delta).toLocaleString()} USD</div></div>
                  {c.settlement.outcome === "shortfall" && <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Refund</span><div className="text-[#2DD4A7]" data-testid="settlement-refund-amount">{Number(c.settlement.refund_amount).toFixed(2)} USDT</div></div>}
                  {c.settlement.outcome === "over" && <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Surcharge (display-only)</span><div className="text-[#F5B841]" data-testid="settlement-surcharge-amount">{Number(c.settlement.surcharge_amount).toFixed(2)} USDT</div></div>}
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Admin</span><div>{c.settlement.admin?.slice(0, 8)}</div></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">When</span><div>{c.settlement.timestamp}</div></div>
                </div>
                {c.settlement.reason && <div className="text-[11px] font-mono text-[#8B97AE]">reason: {c.settlement.reason}</div>}
                {c.settlement.refund_payout_id && (
                  <div className="text-[11px] font-mono text-[#2DD4A7]" data-testid="settlement-payout-link">→ Payout row: {c.settlement.refund_payout_id.slice(0, 8)} (see Payouts Pending, streamer recipient, screening required before paid)</div>
                )}
                {c.settlement.outcome === "over" && (
                  <div className="border border-dashed border-[#F5B841]/50 p-2 text-[11px] font-mono text-[#F5B841]" data-testid="settlement-charge-note">
                    Inbound charge is DISPLAY ONLY — UNLOCK_PAYMENT_ARMED is off; no automated charge is executed.
                  </div>
                )}
                {/* Iter 87 — Player rebate materialization */}
                {typeof c.settlement.player_rebate_final === "number" && c.settlement.player_rebate_final > 0 && (
                  <div className="border border-dashed border-[#2DD4A7]/50 bg-[#2DD4A7]/[0.05] p-3 space-y-2" data-testid="admin-contract-rebate-panel">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">Player cashback (loyalty_rebate)</div>
                    <div className="text-[10px] text-[#8B97AE]" data-testid="admin-rebate-scope">Whole-deal total for the 90-day term (not per month).</div>
                    <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
                      <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Rate</span><div className="text-[#2DD4A7]" data-testid="rebate-rate">{Math.round((c.settlement.player_rebate_rate || 0) * 100)}%</div></div>
                      <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Expected</span><div className="text-[#8B97AE]" data-testid="rebate-expected">{Number(c.settlement.player_rebate_expected || 0).toFixed(2)}</div></div>
                      <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Final (to pay)</span><div className="text-[#2DD4A7]" data-testid="rebate-final">{Number(c.settlement.player_rebate_final).toFixed(2)}</div></div>
                    </div>
                    {c.settlement.rebate_payout_id ? (
                      <div className="text-[11px] font-mono text-[#2DD4A7]" data-testid="rebate-payout-link">
                        → Cashback payout row: {c.settlement.rebate_payout_id.slice(0, 8)} (Payouts tab — manual screening required before paid)
                      </div>
                    ) : (
                      <Button
                        data-testid="admin-contract-create-rebate-payout"
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await api.post(`/admin/contracts/${c.id}/create-rebate-payout`);
                            toast.success("Cashback payout created (pending manual screening)");
                            await refresh();
                            if (onChanged) onChanged();
                          } catch (e) { toast.error(apiErrorMessage(e)); }
                          finally { setBusy(false); }
                        }}
                        disabled={busy}
                        className="rounded-none bg-[#2DD4A7] text-black hover:bg-[#F5B841] h-9 font-mono text-[11px] uppercase tracking-widest"
                      >Create cashback payout</Button>
                    )}
                    <div className="text-[10px] font-mono text-[#8B97AE]">Creates a PENDING loyalty_rebate payout to the player. Manual sanctions screening + tx_ref required before Mark Paid unlocks. No automated on-chain send (PAYOUT_EXECUTION_ARMED=false).</div>
                  </div>
                )}
              </div>
            )}

            {/* Docs tabs — admin sees BOTH */}
            <div>
              <div className="flex gap-2 mb-2">
                {["player", "streamer"].map((k) => (
                  <button
                    key={k}
                    data-testid={`admin-contract-doc-tab-${k}`}
                    onClick={() => setTab(k)}
                    className={`px-3 h-8 border font-mono text-[11px] uppercase tracking-widest transition-colors ${
                      tab === k ? "border-[#2DD4A7] text-[#2DD4A7] bg-[#2DD4A7]/10" : "border-white/20 text-[#8B97AE] hover:text-white"
                    }`}
                  >{k === "player" ? "Player Agreement" : "Streamer Agreement"}</button>
                ))}
              </div>
              {tab === "player"
                ? <DocPanel title="Player Agreement (populated)" doc={c.playerDocument} testIdPrefix="admin-contract-doc-player" />
                : <DocPanel title="Streamer Agreement (populated)" doc={c.streamerDocument} testIdPrefix="admin-contract-doc-streamer" />}
            </div>

            {/* Audit trail */}
            <div className="border border-white/10 p-3" data-testid="admin-contract-audit">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Audit trail (iter3)</div>
              {!c.audit_iter3 || c.audit_iter3.length === 0 ? (
                <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="admin-contract-audit-empty">No audit entries yet.</div>
              ) : (
                <ul className="space-y-2">
                  {[...c.audit_iter3].reverse().map((a, i) => (
                    <li key={i} className="border border-white/10 p-2 text-[11px] font-mono" data-testid={`admin-contract-audit-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-[#8B97AE]">actor: {a.actor?.slice(0, 8) || "—"}</span>
                        <span className="text-[10px] text-[#8B97AE]">{a.timestamp}</span>
                      </div>
                      <div className="text-[#E8ECF4] mt-1">
                        <span className="text-[#F26D6D]">{a.from}</span>
                        <span className="text-[#8B97AE] mx-2">→</span>
                        <span className="text-emerald-400">{a.to}</span>
                      </div>
                      {a.reason && <div className="text-[10px] text-[#8B97AE] mt-1">reason: {a.reason}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
